import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getAuthorizationHeader, http } from '../../../config/http';
import Layouts from '../../../layouts/Layouts';
import { DinamicTitle } from '../../../utils/DinamicTitle';
import { IconEdit, IconTrash, IconUserPlus, IconX } from '@tabler/icons-react';
import { createPortal } from 'react-dom';

export default function KaryawanList() {
    DinamicTitle('Data Karyawan || Komaladewi');
    const [karyawans, setKaryawans] = useState([]);
    const [cabangList, setCabangList] = useState([]);
    const [filterStatus, setFilterStatus] = useState('Semua');
    const [loading, setLoading] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const [formData, setFormData] = useState({
        nama_karyawan: '',
        cabang_id: '',
        no_hp: '',
    });

    const navigate = useNavigate();

    const fetchData = async () => {
        setLoading(true);
        try {
            const [resKaryawan, resCabang] = await Promise.all([
                http.get('/karyawan', { headers: { Authorization: getAuthorizationHeader() } }),
                http.get('/cabang', { headers: { Authorization: getAuthorizationHeader() } })
            ]);
            setKaryawans(resKaryawan.data.data || []);
            setCabangList(resCabang.data.data || resCabang.data || []);
        } catch (err) {
            if (err.response && err.response.status === 401) {
                navigate('/login');
            } else {
                toast.error('Gagal mengambil data karyawan.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [navigate]);

    const filteredData = karyawans.filter(k => {
        const matchStatus = filterStatus === 'Semua' || (filterStatus === 'Aktif' ? k.is_active : !k.is_active);
        return matchStatus;
    });

    const toggleStatus = async (id, currentStatus) => {
        try {
            setLoading(true);
            await http.patch(`/karyawan/${id}/status`, { is_active: !currentStatus }, {
                headers: { Authorization: getAuthorizationHeader() },
            });
            toast.success(`Karyawan berhasil ${!currentStatus ? 'diaktifkan' : 'dinonaktifkan'}.`);
            await fetchData();
        } catch (err) {
            toast.error('Gagal mengubah status.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await http.delete(`/karyawan/${id}`, {
                headers: { Authorization: getAuthorizationHeader() },
            });
            toast.success('Karyawan berhasil dihapus.');
            await fetchData();
        } catch (err) {
            toast.error('Gagal menghapus karyawan.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (editMode) {
                await http.put(`/karyawan/${selectedId}`, formData, {
                    headers: { Authorization: getAuthorizationHeader() },
                });
                toast.success('Karyawan berhasil diperbarui.');
            } else {
                await http.post('/karyawan', formData, {
                    headers: { Authorization: getAuthorizationHeader() },
                });
                toast.success('Karyawan berhasil ditambahkan.');
            }
            setIsModalOpen(false);
            await fetchData();
        } catch (err) {
            if (err.response?.data?.errors) {
                const errors = err.response.data.errors;
                Object.keys(errors).forEach(key => {
                    toast.error(errors[key][0]);
                });
            } else {
                toast.error(`Gagal ${editMode ? 'mengubah' : 'menambah'} karyawan.`);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const openAddModal = () => {
        setEditMode(false);
        setFormData({ nama_karyawan: '', cabang_id: '', no_hp: '' });
        setIsModalOpen(true);
    };

    const openEditModal = (karyawan) => {
        setEditMode(true);
        setSelectedId(karyawan.id);
        setFormData({
            nama_karyawan: karyawan.nama_karyawan,
            cabang_id: karyawan.cabang_id || '',
            no_hp: karyawan.no_hp || '',
        });
        setIsModalOpen(true);
    };

    return (
        <Layouts>
            <ToastContainer autoClose={2000} position="top-right" />
            <div className="p-6">
                <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Data Karyawan</h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Manajemen data karyawan toko.</p>
                    </div>
                    <button
                        onClick={openAddModal}
                        className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl shadow-sm transition-all"
                    >
                        <IconUserPlus size={20} />
                        Tambah Karyawan
                    </button>
                </div>

                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="p-2 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 min-w-[200px]"
                        >
                            <option value="Semua">Semua Status</option>
                            <option value="Aktif">Aktif</option>
                            <option value="Nonaktif">Non-aktif</option>
                        </select>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-200 dark:border-slate-700">
                                    <th className="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">No</th>
                                    <th className="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Nama Karyawan</th>
                                    <th className="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Cabang</th>
                                    <th className="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">No. HP</th>
                                    <th className="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300">Status</th>
                                    <th className="p-3 text-sm font-semibold text-slate-600 dark:text-slate-300 text-center">Aksi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="p-4 text-center text-slate-500">Memuat data...</td>
                                    </tr>
                                ) : filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="p-4 text-center text-slate-500">Tidak ada data.</td>
                                    </tr>
                                ) : (
                                    filteredData.map((item, index) => (
                                        <tr key={item.id} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                                            <td className="p-3 text-sm text-slate-600 dark:text-slate-300">{index + 1}</td>
                                            <td className="p-3 text-sm text-slate-700 dark:text-slate-200 font-medium">{item.nama_karyawan}</td>
                                            <td className="p-3 text-sm text-slate-600 dark:text-slate-300">{item.cabang?.nama_cabang || '-'}</td>
                                            <td className="p-3 text-sm text-slate-600 dark:text-slate-300">{item.no_hp || '-'}</td>
                                            <td className="p-3">
                                                <button
                                                    onClick={() => toggleStatus(item.id, item.is_active)}
                                                    className={`px-3 py-1 rounded-full text-xs font-semibold ${item.is_active ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'}`}
                                                >
                                                    {item.is_active ? 'Aktif' : 'Non-aktif'}
                                                </button>
                                            </td>
                                            <td className="p-3 text-center">
                                                <div className="flex justify-center items-center gap-2">
                                                    <button onClick={() => openEditModal(item)} className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors" title="Edit">
                                                        <IconEdit size={18} />
                                                    </button>
                                                    <button onClick={() => handleDelete(item.id)} className="p-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors" title="Hapus">
                                                        <IconTrash size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal Add/Edit */}
            {isModalOpen && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-xl w-full max-w-md shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="flex justify-between items-center p-6 border-b border-slate-100 dark:border-slate-700">
                            <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                                {editMode ? 'Edit Karyawan' : 'Tambah Karyawan'}
                            </h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                                <IconX size={24} />
                            </button>
                        </div>
                        <div className="p-6 overflow-y-auto">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nama Karyawan *</label>
                                    <input
                                        type="text"
                                        name="nama_karyawan"
                                        value={formData.nama_karyawan}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full p-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Cabang *</label>
                                    <select
                                        name="cabang_id"
                                        value={formData.cabang_id}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full p-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
                                    >
                                        <option value="">-- Pilih Cabang --</option>
                                        {cabangList.filter(c => c.is_toko == 1 || c.is_toko === true).map(c => (
                                            <option key={c.id} value={c.id}>{c.nama_cabang}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">No. HP</label>
                                    <input
                                        type="text"
                                        name="no_hp"
                                        value={formData.no_hp}
                                        onChange={handleInputChange}
                                        className="w-full p-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
                                    />
                                </div>
                                <div className="pt-4 flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="px-5 py-2.5 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700 rounded-xl font-medium transition-colors"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
                                    >
                                        {isSubmitting ? 'Menyimpan...' : 'Simpan'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </Layouts>
    );
}
